import LegalPage, { LegalSection, LegalLi } from "@/pages/legal/LegalPage";

const Privacy = () => (
  <LegalPage
      kicker="Trail policies"
      title="Privacy Policy"
      updated="May 14, 2025"
      version="2025-05-14"
      otherLabel="Terms of Service"
      otherTo="/terms"
    >
      <LegalSection id="intro" title="1. Who we are">
        <p>
          Mind Map is an educational platform operated by Jeremy Meadows under the brand
          "The Meadows Mindset" (we, us, our). Mind Map helps K–12 students learn through
          guided "expeditions" — short, AI-assisted learning paths covering vocabulary,
          quizzes, flashcards, and activities. You can reach us any time at{" "}
          <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>.
        </p>
        <p>
          This policy describes what personal information we collect, how we use it, who
          we share it with, and the choices you have. By using Mind Map you agree to the
          practices described here.
        </p>
      </LegalSection>

      <LegalSection id="kids" title="2. Children's privacy and COPPA compliance">
        <p>
          Mind Map is built for K–12 learners. Many of our users are children under 13.
          We comply with the U.S. Children's Online Privacy Protection Act ("COPPA")
          and design Mind Map so that the only path for a child to use the service is
          through a parent, teacher, or other authorized adult who has agreed to these
          terms on their behalf.
        </p>
        <ul>
          <LegalLi>
            <strong>Adult-managed accounts.</strong> Mind Map accounts are created and owned
            by parents, guardians, or educators ("Parent/Educator"). The Parent/Educator
            agrees to this Privacy Policy and our Terms of Service on the child's behalf
            and is responsible for the child's use of Mind Map.
          </LegalLi>
          <LegalLi>
            <strong>Child profiles ("climbers").</strong> Inside an adult-managed account,
            the Parent/Educator may create one or more child profiles ("climbers"). For
            each climber we collect only a first name (or nickname), a grade level, and
            educational progress data (Altitude Points, badges, streaks, expedition
            answers). We do not ask children for their last name, email, phone number,
            home address, photos, or any other personally identifying information.
          </LegalLi>
          <LegalLi>
            <strong>No direct marketing to children.</strong> We never serve ads to climbers,
            never sell or rent child data, and never use child data for behavioral
            advertising of any kind.
          </LegalLi>
          <LegalLi>
            <strong>Parental rights.</strong> A Parent/Educator may at any time review their
            child's profile data, request a correction, or delete the climber and all
            associated data by removing the climber from the Mind Map dashboard or by
            emailing us at{" "}
            <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>.
            We will honor verified deletion requests promptly.
          </LegalLi>
          <LegalLi>
            <strong>Verifiable parental consent.</strong> Account creation requires the
            adult to provide a working email address, agree to this policy, and accept the
            Terms of Service. The adult's billing relationship (handled through Stripe for
            paid plans) provides an additional layer of consent verification.
          </LegalLi>
        </ul>
        <p>
          If you believe a child has created a Mind Map account or submitted personal
          information without proper parental consent, please contact us at{" "}
          <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>{" "}
          and we will delete the account.
        </p>
      </LegalSection>

      <LegalSection id="data-we-collect" title="3. Information we collect">
        <p><strong>From the Parent/Educator who owns the account:</strong></p>
        <ul>
          <LegalLi>Name and email address (for sign-in and account communications).</LegalLi>
          <LegalLi>A hashed password (we never store the plain-text password).</LegalLi>
          <LegalLi>Subscription state, billing status, and plan history (synced from Stripe).</LegalLi>
          <LegalLi>Records of agreement to these terms (date, version).</LegalLi>
          <LegalLi>Basic usage diagnostics (timestamps of logins, last activity).</LegalLi>
        </ul>
        <p><strong>From each climber profile:</strong></p>
        <ul>
          <LegalLi>First name or nickname, grade level, and (optional) avatar selection.</LegalLi>
          <LegalLi>Learning progress: completed expeditions, scores, Altitude Points, badges, streaks.</LegalLi>
          <LegalLi>Free-text answers to expedition prompts (e.g., a Quick Write response). These are stored to score the activity and may be reviewed by the Parent/Educator on the dashboard.</LegalLi>
        </ul>
        <p><strong>Automatically collected:</strong></p>
        <ul>
          <LegalLi>Basic device and browser metadata (user agent, screen size) used solely for compatibility and to render the Progressive Web App.</LegalLi>
          <LegalLi>Server-side logs of API requests (IP, endpoint, status code) retained for up to 30 days for security and debugging.</LegalLi>
        </ul>
        <p>
          We do <strong>not</strong> use third-party advertising trackers and we do
          <strong> not</strong> embed third-party analytics that profile individuals.
        </p>
      </LegalSection>

      <LegalSection id="how-we-use" title="4. How we use information">
        <p>We use the information we collect to:</p>
        <ul>
          <LegalLi>Provide, operate, and personalize the Mind Map learning experience.</LegalLi>
          <LegalLi>Generate expedition content tuned to a climber's grade level.</LegalLi>
          <LegalLi>Track Altitude Points, badges, streaks, and other gamified progress so the Parent/Educator can see how the climber is doing.</LegalLi>
          <LegalLi>Process subscription payments and prevent billing fraud through Stripe.</LegalLi>
          <LegalLi>Send transactional and lifecycle emails (welcome, password reset, trial reminders, subscription receipts, weekly progress reports). You can unsubscribe from non-essential emails at any time.</LegalLi>
          <LegalLi>Maintain account security, detect abuse, and enforce our Terms of Service.</LegalLi>
          <LegalLi>Comply with legal obligations and respond to lawful requests.</LegalLi>
        </ul>
      </LegalSection>

      <LegalSection id="service-providers" title="5. Service providers we share data with">
        <p>
          We use a small number of vendors to operate Mind Map. We share only the data
          each vendor needs to do its job, and only under contracts that require them to
          protect your information.
        </p>
        <ul>
          <LegalLi>
            <strong>Stripe</strong> processes subscription payments. When you upgrade to
            a paid plan, Stripe receives your name, email, billing address, and payment
            method. Mind Map never stores full credit card numbers — only a Stripe
            customer ID and the high-level subscription state.
          </LegalLi>
          <LegalLi>
            <strong>Resend</strong> delivers transactional and lifecycle emails on our
            behalf. Resend receives the recipient email address and the email content
            for each message we send you.
          </LegalLi>
          <LegalLi>
            <strong>Anthropic (Claude API)</strong> generates the educational text used
            inside expeditions (vocabulary, quizzes, guide narration). When a climber or
            Parent/Educator triggers expedition generation, Mind Map sends Anthropic the
            topic, grade level, and difficulty — never the climber's name, the account
            email, or any other identifying information. Anthropic processes these
            requests under their commercial terms and does not use submitted content to
            train their public models.
          </LegalLi>
          <LegalLi>
            <strong>Cloud hosting</strong> providers run our servers and database. They
            see only what is necessary to operate the service.
          </LegalLi>
        </ul>
        <p>
          We do not sell, rent, or trade personal information to advertisers or data
          brokers. Period.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="6. How long we keep data">
        <p>
          We keep account and climber data for as long as the account is active. When
          you delete a climber, the climber profile and associated expeditions are
          removed from active databases promptly. When you close your account, the
          account and all owned climbers are deleted (best-effort cancel of any active
          Stripe subscription happens automatically). Email delivery logs and payment
          records may be retained for a longer period as required by tax and accounting
          law.
        </p>
      </LegalSection>

      <LegalSection id="security" title="7. Security">
        <p>
          We protect your account with bcrypt password hashing, encrypted connections
          (HTTPS/TLS) for all data in transit, and least-privilege access controls
          inside our infrastructure. We also operate an AI content-safety layer that
          filters expedition inputs and outputs to keep the platform age-appropriate.
        </p>
        <p>
          No online service can promise perfect security. If we ever discover a security
          incident that affects your account, we will notify you by email and on the
          dashboard as soon as we reasonably can.
        </p>
      </LegalSection>

      <LegalSection id="your-rights" title="8. Your rights and choices">
        <ul>
          <LegalLi><strong>Access and correction.</strong> You can view and update your account information at any time from the My Account page.</LegalLi>
          <LegalLi><strong>Deletion.</strong> You can remove a climber from the dashboard or delete your entire account by emailing <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>.</LegalLi>
          <LegalLi><strong>Unsubscribe.</strong> Every non-essential email includes an unsubscribe link. We will continue to send essential transactional emails (password reset, payment receipts).</LegalLi>
          <LegalLi><strong>Export.</strong> Email us if you want a copy of the data on your account; we will provide it in a portable format within 30 days.</LegalLi>
          <LegalLi><strong>California, EU, and other jurisdictions.</strong> If you live in a place that grants you specific privacy rights (e.g., the right to know what we have, the right to delete, the right to object to processing), email us and we will honor those rights to the extent the law requires.</LegalLi>
        </ul>
      </LegalSection>

      <LegalSection id="changes" title="9. Changes to this policy">
        <p>
          We may update this policy from time to time. When we do, we will update the
          "last updated" date at the top and post the new version at this URL. If the
          changes are material, we will notify you by email. Continued use of Mind Map
          after the changes go into effect constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="10. Contact us">
        <p>
          Questions, requests, or privacy concerns? Email Jeremy Meadows at{" "}
          <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>.
          We aim to respond within five business days.
        </p>
      </LegalSection>
    </LegalPage>
);

export default Privacy;
